import { getBitmap, getBitmapsForTag } from '../../../../data/analyticsDataSlice';
import { TpDataContext, VALTYPES, VALTYPE_ANY } from '../../../../data/interfaces';
import { createColorMappingAspect, createColorMappingConfigs } from '../../helpers/colorMappingInfo';
import { CONFIGPARAMTYPES, createConfigParamBoolean, createConfigParamTypeRange } from '../../helpers/configParams';
import { TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  CONFIGPARAM_VISIBILITYCONDITION_TYPES,
  DOCVIEWER_ELEMENT_TYPES,
  TpMarkdownDocViewerElement,
  TpPropertyDocViewerElement,
  TpChartManifest,
} from '../interfaces';
import { ScatterPlot } from './ScatterPlot';

export const manifestScatterPlot: TpChartManifest = {
  id: 'ScatterPlot',
  name: 'Scatter plot',
  description: `Visualizes the **relationship** between **two numerical properties**. Each entity is shown as a point, with the X and Y coordinates taken from those numerical properties.
Optionally, these points can be color coded by another property, and the size of the points can be scaled by another numerical property.

Example usage:
- UMAP plot using precomputed X and Y coordinates.
- Volcano plot, visualizing log fold change versus -log of p-values.
`,
  component: ScatterPlot,
  canvasBased: true,
  groups: [
    { groupId: 'axes', groupName: 'Axes' },
    { groupId: 'pointColor', groupName: 'Point color' },
    { groupId: 'pointLayout', groupName: 'Point layout' },
    { groupId: 'advanced', groupName: 'Advanced' },
  ],
  aspects: [
    {
      id: 'x',
      name: 'X axis data',
      description: 'X coordinate used for each data point.',
      groupId: 'axes',
      required: true,
      quality: 1,
      dataType: [VALTYPES.NUMBER, VALTYPES.INTEGER],
    },
    {
      id: 'y',
      name: 'Y axis data',
      description: 'Y coordinate used for each data point.',
      groupId: 'axes',
      required: true,
      quality: 0.95,
      dataType: [VALTYPES.NUMBER, VALTYPES.INTEGER],
    },
    {
      id: 'size',
      name: 'Point size data',
      description: 'Makes sizes of the points in the scatterplot proportional to a numerical property.',
      groupId: 'pointLayout',
      required: false,
      quality: 0.25,
      dataType: [VALTYPES.NUMBER, VALTYPES.INTEGER],
    },
    createColorMappingAspect('color', 'Point color data', 'pointColor', 0.5, 'data points'),
    {
      id: 'hoverLabel',
      name: 'Hover label',
      description: 'Text to be displayed when the mouse hovers over a data point.',
      groupId: 'pointLayout',
      required: false,
      dataType: VALTYPE_ANY,
    },
  ],
  configParams: [
    {
      id: 'pointSizeFactor',
      name: 'Point size',
      description: 'Scaling factor for the overall size of the points in the plot.',
      groupId: 'pointLayout',
      paramType: createConfigParamTypeRange(0.2, 4, 0.1, 1),
    },
    {
      id: 'opacity',
      name: 'Opacity',
      description: `Point opacity.
In case a plot contains many overlapping points, lowering the opacity (i.e. increasing transparency)
can help reveal the data structure.`,
      groupId: 'pointLayout',
      paramType: createConfigParamTypeRange(0.1, 1, 0.1, 0.7),
    },
    {
      id: 'unselectedOpacity',
      name: 'Unselected Opacity',
      groupId: 'pointLayout',
      description: `Unselected point opacity.`,
      paramType: createConfigParamTypeRange(0.01, 1, 0.1, 0.7),
    },
    ...createColorMappingConfigs('color', 'Color', 'pointColor'),
    {
      id: 'backgroundImageId',
      name: 'Background Image ID',
      groupId: 'axes',
      hidden: true,
      description:
        'Optional bitmap ID that will be rendered in the background. ' +
        'The coordinates defined in the bitmap manifest should have the same unit as those of the axes aspects. ' +
        'If combined with a background image tag, this bitmap ID will be selected by default',
      paramType: { configParamTypeId: CONFIGPARAMTYPES.String },
    },
    {
      id: 'backgroundImageTag',
      name: 'Background Image Tag',
      groupId: 'axes',
      hidden: true,
      description:
        'Optional bitmap tag. If present, the background image config will be shown to select a background image from those with the given tag.',
      paramType: { configParamTypeId: CONFIGPARAMTYPES.String },
    },
    {
      id: 'backgroundImage',
      name: 'Background Image',
      groupId: 'axes',
      description: 'Selected background image.',
      paramType: createBitmapParamType,
      documentation: createBitmapDocumentation,
      visibilityConditions: [
        {
          conditionType: CONFIGPARAM_VISIBILITYCONDITION_TYPES.ANY_CONFIG_PRESENT,
          conditionInfo: {
            configParams: ['backgroundImageId', 'backgroundImageTag'],
          },
        },
      ],
    },
    {
      id: 'backgroundBitmapOpacity',
      name: 'Background Image Opacity',
      groupId: 'axes',
      description: 'Opacity of the displayed background image',
      paramType: createConfigParamTypeRange(0.01, 1.001, 0.025, 1),
      visibilityConditions: [
        {
          conditionType: CONFIGPARAM_VISIBILITYCONDITION_TYPES.CONFIG_PRESENT,
          conditionInfo: {
            configParam: 'backgroundImage',
          },
        },
      ],
    },
    {
      id: 'hideAxes',
      name: 'Hide axes',
      groupId: 'advanced',
      description: 'If set, the X and Y axis of the scatter plot are not displayed.',
      paramType: createConfigParamBoolean(false),
    },
    {
      id: 'hideColorLegend',
      name: 'Hide color legend',
      description: 'If set, colors used for the data points are not represented in the legend.',
      groupId: 'advanced',
      paramType: createConfigParamBoolean(false),
    },
    {
      id: 'forceSquareApectRatio',
      name: 'Square aspect ratio',
      description: 'If set, the X and Y axes preserve the same units of length.',
      groupId: 'advanced',
      paramType: createConfigParamBoolean(false),
    },
    {
      id: 'zoomPanXYGroup',
      name: 'Zoom & Pan synchronization group',
      groupId: 'advanced',
      hidden: true,
      description:
        'This parameter can be used to synchronize the zooming and panning of a group of scatterplots, by setting the same identifier to each plot in the group. This will cause user zoom and pan operations on one chart to be automatically applied to all other charts in the group.',
      paramType: { configParamTypeId: CONFIGPARAMTYPES.String },
    },
    {
      id: 'backgroundColor',
      name: 'Background color',
      description:
        'Background color for the plot. Labels and grid lines are automatically adjusted in case of a dark color',
      hidden: true,
      groupId: 'advanced',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.JSON,
        schema: {
          type: 'array',
          minItems: 3,
          maxItems: 3,
          description: 'An array of 3 RGB values between 0 and 255',
          items: { type: 'number' },
        },
      },
    },
  ],
  examples: [
    {
      id: 'scatter_1',
    },
    {
      id: 'scatter_2',
    },
    {
      id: 'scatter_3',
    },
    {
      id: 'scatter_4',
    },
  ],
};

type TpChoiceItem = {
  id: string;
  name: string;
};

const NO_BITMAP_SELECTED_ID = '';

function getDefaultBitmapId(currentConfigParams: TpChartConfigParams) {
  return currentConfigParams['backgroundImageId'] ?? NO_BITMAP_SELECTED_ID;
}

function createBitmapParamType(
  currentConfigParams: TpChartConfigParams,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
) {
  let choices: TpChoiceItem[] = [];

  const bitmapTag = currentConfigParams['backgroundImageTag'];
  if (bitmapTag) {
    const bitmaps = getBitmapsForTag(dataContext, bitmapTag);
    choices = bitmaps.map((bitmap) => ({ id: bitmap.id, name: bitmap.name }));
  }

  const defaultBackgroundImageId = getDefaultBitmapId(currentConfigParams);
  if (defaultBackgroundImageId) {
    const defaultBackgroundImage = getBitmap(dataContext, defaultBackgroundImageId);
    if (!bitmapTag || !defaultBackgroundImage.tags.includes(bitmapTag)) {
      choices.push({ id: defaultBackgroundImageId, name: defaultBackgroundImage.name });
    }
  }

  choices.sort((a, b) => a.name.localeCompare(b.name));

  return {
    configParamTypeId: CONFIGPARAMTYPES.MultipleChoice,
    choices: [
      {
        id: NO_BITMAP_SELECTED_ID,
        name: 'No background',
      },
      ...choices,
    ],
    defaultValue: defaultBackgroundImageId ?? '',
  };
}

function createBitmapDocumentation(
  currentConfigParams: TpChartConfigParams,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
) {
  const bitmapId = currentConfigParams['backgroundImage'] ?? getDefaultBitmapId(currentConfigParams);
  if (!bitmapId || bitmapId === NO_BITMAP_SELECTED_ID) {
    const placeHolderElement: TpMarkdownDocViewerElement = {
      type: DOCVIEWER_ELEMENT_TYPES.STRING,
      id: 'backgroundImage',
      content: '*No background image selected.*',
    };
    return [placeHolderElement];
  }
  const bitmap = getBitmap(dataContext, bitmapId);
  const propertyElement: TpPropertyDocViewerElement = {
    type: DOCVIEWER_ELEMENT_TYPES.PROPERTY,
    id: 'backgroundImage',
    content: {
      name: bitmap.name,
      description: bitmap.description,
    },
  };
  return [propertyElement];
}
